import humanizeDuration from "humanize-duration";
import { get, isNumber } from "lodash";
import moment from "moment";
import { numberFormat } from "underscore.string";

import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";

export const formatAmountCompact = (
  value: number | null,
  options?: { precision: number },
): string => {
  if (!isNumber(value)) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  if (value === 0) {
    return String(value);
  }

  const epsilon = value > 1000 ? 0.0001 : 0; // ks need to be increased when rounding based on significant digits
  const ks = Math.floor(Math.log(value) / Math.log(1000) + epsilon);

  const powerDown = value / Math.pow(1000, ks);
  const displayAmount = !!options
    ? powerDown.toPrecision(options?.precision ?? 1)
    : Math.round(powerDown);
  switch (ks) {
    case 1:
      return `${displayAmount}K`;
    case 2:
      return `${displayAmount}M`;
    case 3:
      return `${displayAmount}B`;
    default:
      return String(displayAmount);
  }
};

export const formatAmount = (value: number | string | null, dc?: number) =>
  value != null
    ? `$${numberFormat(Number(value), isNumber(dc) ? dc : 2)}`
    : EMPTY_VALUE_PLACEHOLDER;

const currencySignMap = {
  USD: "$",
  ZWL: "$",
  ETH: "ETH",
};
const formatCurrencySign = (currency: string) =>
  get(currencySignMap, currency, currency);

export const formatCurrencyString = (currency: string, value: string) => {
  const currencySign = formatCurrencySign(currency);
  return currencySign === "$"
    ? `${currencySign}${value}`
    : `${value} ${currencySign}`;
};

export const formatCurrencyAmount = (
  currency: string,
  value: number | string | null,
  dc?: number,
  compactOptions?: { precision: number },
) => {
  const parsedValue = Number(value);
  const isNegative = parsedValue < 0;
  const absAmount = Math.abs(parsedValue);

  const standardDecimalPlaces = formatCurrencySign(currency) === "$" ? 0 : 3;
  const amount = !!compactOptions
    ? formatAmountCompact(absAmount, compactOptions)
    : numberFormat(absAmount, dc ?? standardDecimalPlaces);
  return value !== null
    ? `${isNegative ? "-" : ""}${formatCurrencyString(currency, amount)}`
    : EMPTY_VALUE_PLACEHOLDER;
};

export const formatDate = (value: string | null) =>
  value ? `${moment.utc(value).format("MM/YYYY")}` : EMPTY_VALUE_PLACEHOLDER;

export const formatHumanDate = (value: string | null, compact = false) => {
  const format = compact ? "MMM D, YYYY" : "MMMM D, YYYY";
  return value
    ? `${moment.utc(value).format(format)}`
    : EMPTY_VALUE_PLACEHOLDER;
};

export const formatESTDate = (value: string | null) =>
  value
    ? `${
        moment.utc(value).subtract(5, "hours").format("MMMM D, YYYY hh:mm") +
        " EST"
      }`
    : EMPTY_VALUE_PLACEHOLDER;

export const formatNumber = (
  value: number | null,
  suffix?: string,
  dc?: number,
) =>
  value || value === 0
    ? `${numberFormat(Number(value), isNumber(dc) ? dc : 2)}${suffix || ""}`
    : EMPTY_VALUE_PLACEHOLDER;

export const formatPercentage = (value: number | null) =>
  isNumber(value)
    ? `${parseFloat((value * 100).toFixed(2))}%`
    : EMPTY_VALUE_PLACEHOLDER;

const getMoment = (value: string | number) =>
  typeof value === "number" ? moment.unix(value) : moment.utc(value);

export const formatDateRange = (
  f: string | number,
  t: string | number,
  largest = 1,
) => {
  if (f && t) {
    const timeLeftMilliseconds = getMoment(f).diff(getMoment(t), "ms");

    const timeLeftHours = getMoment(f).diff(getMoment(t), "h");

    const showHours = timeLeftHours >= 24 && timeLeftHours <= 48;

    return humanizeDuration(timeLeftMilliseconds, {
      largest,
      units: showHours ? ["h", "m"] : ["y", "d", "h", "m", "s"],
      maxDecimalPoints: 0,
    });
  } else {
    return EMPTY_VALUE_PLACEHOLDER;
  }
};

export const formatDateRangeCompact = (
  f: string | number,
  t: string | number,
  onlyHour = false,
) => {
  const fromMoment = getMoment(f);
  const toMoment = getMoment(t);

  if (fromMoment.isValid() && toMoment.isValid()) {
    const timeLeftSeconds = fromMoment.diff(toMoment, "s");

    if (timeLeftSeconds <= 0) {
      return EMPTY_VALUE_PLACEHOLDER;
    }

    const hours = Math.floor(timeLeftSeconds / 3600)
      .toString()
      .padStart(2, "0");
    const minutes = Math.floor((timeLeftSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const seconds = ((timeLeftSeconds % 3600) % 60).toString().padStart(2, "0");

    return onlyHour ? `${hours}` : `${hours}:${minutes}:${seconds}`;
  } else {
    return EMPTY_VALUE_PLACEHOLDER;
  }
};

export const formatMarketplaceMedianAmountSource = (value: string | null) =>
  value === "three_years_average"
    ? "3-Year Average"
    : value === "ltm"
    ? "LTM"
    : "";

export const formatOneDecimalOmitZero = (n: number): string => {
  let stringRepresentation = n.toFixed(1);
  if (stringRepresentation.endsWith(".0")) {
    stringRepresentation = stringRepresentation.slice(0, -2);
  }

  return stringRepresentation;
};

export const formatMarketplaceMedian = (offer: string | null) => {
  if (typeof offer !== "string") {
    // marketplace median is disabled
    return EMPTY_VALUE_PLACEHOLDER;
  }
  const offerNumber = Number(offer);
  if (Number.isNaN(offerNumber)) {
    // `offer` is the placeholder string
    return formatCurrencyString("USD", offer);
  }
  return formatCurrencyAmount("USD", offerNumber);
};
